import { useState } from 'react';

/**
 * Custom React hook for handling clipboard functionality.
 *
 * @param {Object} options - Optional timeout configuration for clipboard copy.
 * @param {number} options.timeout - Timeout duration in milliseconds.
 * @return {Object} An object containing copy and reset functions, error state, and copied state.
 */
function useClipboard({ timeout = 1000 } = {}) {
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyTimeout, setCopyTimeout] = useState(null);

  /**
   * Handle the copying result.
   *
   * @param {type} value - the value to be copied
   * @return {type} undefined
   */
  const handleCopyResult = (value) => {
    window.clearTimeout(copyTimeout);
    setCopyTimeout(window.setTimeout(() => setCopied(false), timeout));
    setCopied(value);
  };

  /**
   * Copies the given value to the clipboard if supported, otherwise sets an error.
   *
   * @param {string} valueToCopy - The value to be copied to the clipboard
   * @return {void}
   */
  const copy = (valueToCopy) => {
    if ('clipboard' in navigator) {
      navigator.clipboard
        .writeText(valueToCopy)
        .then(() => handleCopyResult(true))
        .catch((err) => setError(err));
    } else {
      setError(new Error('useClipboard: navigator.clipboard is not supported'));
    }
  };

  /**
   * Resets the state by setting copied to false, clearing any error, and clearing the copy timeout.
   */
  const reset = () => {
    setCopied(false);
    setError(null);
    window.clearTimeout(copyTimeout);
  };

  return { copy, reset, error, copied };
}

export { useClipboard };
